import { Plus, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { JOINING_DOCUMENT_LABEL_MAX, JOINING_DOCUMENT_STARTERS, JOINING_DOCUMENTS_MAX, type JoiningDocument } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Checkbox } from '../../../components/Checkbox';
import { Input } from '../../../components/Input';
import styles from './JoiningDocumentsEditor.module.css';

/**
 * Step 7's "Documents before joining" (S24 Q2, §L11). The starter list is ticked on or off;
 * each line on the list can be marked required — joining can't be confirmed until those are
 * verified. The job's own documents are added by name.
 */
export function JoiningDocumentsEditor({ value, isReadOnly, onChange }: { readonly value: readonly JoiningDocument[]; readonly isReadOnly: boolean; readonly onChange: (next: readonly JoiningDocument[]) => void }): ReactNode {
  const [draft, setDraft] = useState('');
  const starterKeys = new Set<string>(JOINING_DOCUMENT_STARTERS.map((starter) => starter.key));
  const own = value.filter((document) => !starterKeys.has(document.key));
  const label = draft.trim();
  const isDuplicate = value.some((document) => document.label.toLowerCase() === label.toLowerCase());
  const canAdd = !isReadOnly && label.length >= 2 && !isDuplicate && value.length < JOINING_DOCUMENTS_MAX;

  function toggleStarter(starter: JoiningDocument, isAsked: boolean): void {
    // The starters keep their order at the top; the job's own follow.
    const next = JOINING_DOCUMENT_STARTERS.flatMap((row) => {
      if (row.key === starter.key) return isAsked ? [{ ...row }] : [];
      const current = value.find((document) => document.key === row.key);
      return current ? [current] : [];
    });
    onChange([...next, ...own]);
  }

  function setRequired(key: string, isRequired: boolean): void {
    onChange(value.map((document) => (document.key === key ? { ...document, isRequired } : document)));
  }

  function add(): void {
    if (!canAdd) return;
    onChange([...value, { key: `own_${Date.now().toString(36)}`, label, isRequired: true }]);
    setDraft('');
  }

  return (
    <div className={styles.editor}>
      <ul className={styles.list} aria-label="Documents before joining">
        {JOINING_DOCUMENT_STARTERS.map((starter) => {
          const current = value.find((document) => document.key === starter.key);
          return (
            <li key={starter.key} className={styles.row}>
              <Checkbox checked={current !== undefined} disabled={isReadOnly} label={starter.label} onChange={(isAsked) => toggleStarter(starter, isAsked)} />
              {current && <Checkbox checked={current.isRequired} disabled={isReadOnly} label="Required" ariaLabel={`${starter.label} required before joining`} onChange={(isRequired) => setRequired(starter.key, isRequired)} />}
            </li>
          );
        })}
        {own.map((document) => (
          <li key={document.key} className={styles.row}>
            <span className={styles.name}>{document.label}</span>
            <span className={styles.ownActions}>
              <Checkbox checked={document.isRequired} disabled={isReadOnly} label="Required" ariaLabel={`${document.label} required before joining`} onChange={(isRequired) => setRequired(document.key, isRequired)} />
              {!isReadOnly && (
                <Button variant="ghost" size="sm" icon={X} aria-label={`Remove ${document.label}`} onClick={() => onChange(value.filter((row) => row.key !== document.key))}>
                  Remove
                </Button>
              )}
            </span>
          </li>
        ))}
      </ul>
      {!isReadOnly && (
        <form
          className={styles.add}
          onSubmit={(event) => {
            event.preventDefault();
            add();
          }}
        >
          <Input aria-label="Another document" maxLength={JOINING_DOCUMENT_LABEL_MAX} value={draft} isInvalid={isDuplicate} placeholder="Another document, e.g. Police clearance" onChange={(event) => setDraft(event.target.value)} />
          <Button variant="outline" icon={Plus} type="submit" disabled={!canAdd}>
            Add
          </Button>
        </form>
      )}
    </div>
  );
}
